import {
  ErrorI,
  isEmail
} from "get-life-storybook-ts/lib/components/Validations";
import { useEffect, useRef, useState } from "react";
import { useDevice } from "get-life-storybook-ts/lib/components/Hooks";
import { posthogCapture, posthogIdentify } from "../../helpers/posthog";
import { pushToDataLayer } from "../../helpers/gtm";
import { getCookie, getReferrerUrl, setCookie as setCookieHook } from "../Hooks";
import prepareCapitals from "../../helpers/Capitals/prepareCapitals";
import { sha256 } from "../../helpers/String/sha256";
import { ulid } from "ulid";
import { useTracker } from "../../contexts/TrackingContext";
import {
  buildAppHandoffUrl,
  navigateToApp,
} from "../../helpers/appHandoff";

interface DataTrackerI {
  leadUlid?: string;
  campaign: string;
  utm_campaign?: string | null;
  utm_source?: string | null;
  utm_medium?: string | null;
  tid?: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  utm_test?: string | null;
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  msclkid?: string | null;
  fbclid?: string | null;
  entryPage?: string | null;
  referrerUrl?: string | null;
  ABTestVersion: string | null;
}

interface FsEventI {
  FsEvent: string;
  data?: {
    leadId?: string;
  };
}

interface IdentifyFSI {
  lead: string;
  data: {
    email: string;
    emailDomain: string;
    brokerId: string;
  };
}
interface ContextProps {
  handleTrack: (value: HandleTrackerI) => void;
  identifyFS: (value: IdentifyFSI) => void;
}

interface HandleTrackerI {
  type: "FS" | "tracker" | "GA";
  data: any;
  event?: string;
}

export interface StrapiDataCalculator {
  data: {
    Url: string;
    Promo?: string;
    backgroundColor?: string;
    HasPhoneValidation?: boolean;
    HasSeriousIllnesses?: boolean;
  }[];
}
interface Validations extends ErrorI<string | number> {}

interface GlobalInfoI {
  defaultcap: string | null;
  gtf_campaign: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_content?: string | null;
  utm_term?: string | null;
  utm_test?: string | null;
  gclid?: string | null;
  gbraid?: string | null;
  wbraid?: string | null;
  msclkid?: string | null;
  fbclid?: string | null;
  tid?: string | null;
  url: any;
  entryPage?: string | null;
  referrerUrl?: string | null;
  ABTestVersion: string | null;
  ucc?: string | null
}
interface FormDataFirstI {
  birthDate: string;
  capital: number | string;
  haveInsurance: string;
}

interface FormDataSecondI {
  phone: string;
  email: string;
  checkedConditions: string | boolean;
  checkedConditions2: string | boolean;
}

const optionsHaveInsurance = [
  {
    label: "Si",
    value: "yes",
  },
  {
    label: "No",
    value: "no",
  },
];

/**
 * El backend no ha creado el lead (cualquier respuesta que no sea 2xx ni 409).
 * Se lanza desde signupOrganic para que fetchOrganic NO siga adelante: sin
 * lead no hay leadCreated, no hay handoff a la app y el usuario ve un error.
 * (12-sep-2026: api.axa.life5.es devolvía 500 en cada alta y la web seguía
 * como si nada — 179 leads de Google Ads perdidos sin que nadie lo viera.)
 */
class LeadSubmissionError extends Error {
  constructor(public readonly status: number) {
    super(`lead submission failed with HTTP ${status}`);
    this.name = "LeadSubmissionError";
  }
}

const errorMessages: any = {
  submission:
    "No hemos podido calcular tu precio en este momento. Inténtalo de nuevo en unos segundos o llámanos.",
  birthDate: "La edad introducida no es asegurable",
  haveInsurance: "Por favor seleccione una opción",
  capital: "Por favor seleccione un capital",
  phone: "Por favor inserte un número de teléfono correcto",
  email: "Por favor inserte un correo electrónico correcto",
  checkedConditions: "Debe aceptar el tratamiento de datos para continuar",
  checkedConditions2: "Debe aceptar las condiciones para continuar",
};

const CalculatorModuleController = ({ data }: StrapiDataCalculator) => {
  const DOMAIN = process.env.GATSBY_APP_LOCALE;
  const isNotDEV = process.env.REACT_APP_ENVIROMENT !== "DEV";
  const HasSeriousIllnesses =
    data[0]?.HasSeriousIllnesses && DOMAIN === "axa_wl_es";
  const [globalInfo, setGlobalInfo] = useState<GlobalInfoI>({
    defaultcap: null,
    gtf_campaign: HasSeriousIllnesses ? null : data[0]?.Promo || getCookie("gtf_campaign") || "3MESES1E",
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    tid: null,
    url: null,
    utm_content: null,
    utm_term: null,
    utm_test: null,
    gclid: null,
    gbraid: null,
    wbraid: null,
    msclkid: null,
    fbclid: null,
    entryPage: null,
    referrerUrl: null,
    ABTestVersion: null,
    ucc: null
  });
  const [formDataFirst, setFormDataFirst] = useState<FormDataFirstI>({
    birthDate: "",
    capital: 0,
    haveInsurance: "",
  });
  const [formDataSecond, setFormDataSecond] = useState<FormDataSecondI>({
    phone: "",
    email: "",
    checkedConditions: "",
    checkedConditions2: "",
  });
  const [formError, setFormError] = useState<any>();
  const [capitalOptions, setCapitalOptions] = useState<any>([]);
  const [actualStep, setActualStep] = useState(1);
  const [loading, setLoading] = useState<boolean>(false);
  const [display, setDisplay] = useState<boolean>(false);
  const [leadId, setLeadId] = useState<string>("");
  const { isMobile, isDesktop, isTablet } = useDevice();
  const { track } = useTracker();
  const stepStartTime = useRef<number>(Date.now());
  const focusedFields = useRef<Set<string>>(new Set());
  const [cookies, setCookie] = useState("");
  const [isFirstIncoming, setIsFirstIncoming] = useState<boolean>(false);

  // const HasPhoneValidation =
  //   data[0]?.HasPhoneValidation && !(globalInfo?.utm_source?.includes("feebbo"))
  //     ? true
  //     : false;

  const HasPhoneValidation = false;
  
  const refPhoneValidationModal = useRef<any>();

  const TogglePhoneValidationModal = () =>
    refPhoneValidationModal.current.handleShowPortal();

  const handleFocus = (field?: string) => {
    const dataInfo = {
      event: "virtualPageview",
      page: window.location.href + " - " + actualStep,
      pageTitle: `APP Calculator - Paso ${actualStep}`,
      answeredQ: "",
    };

    handleTrack({ type: "GA", data: dataInfo });

    handleTrack({
      type: "FS",
      event: `Calculator Step ${actualStep} ${
        actualStep === 1 ? "Interacted" : "Viewed"
      }`,
      data: {},
    });

    if (field && !focusedFields.current.has(field)) {
      focusedFields.current.add(field);
      track("field_focused", { field, step: actualStep, calculator: "es" });
    }
  };

  const fieldValidation = async ({ data, key, newError }: any) => {
    if (key !== "redirect") {
      if (!data[key]) {
        newError = {
          ...newError,
          [key]: { error: true, message: errorMessages[key] },
        };
      } else if (key === "phone") {
        // Validar que solo contenga números (y opcionalmente espacios, guiones, paréntesis o +)
        const phoneRegex = /^[\d\s\-\(\)\+]+$/;
        if (!phoneRegex.test(data[key])) {
          newError = {
            ...newError,
            [key]: { error: true, message: errorMessages[key] },
          };
        } else {
          newError = { ...newError, [key]: { error: false, message: "" } };
        }
      } else if (key === "email") {
        const validEmail = await validateEmail(data[key]);
        if (!validEmail) {
        newError = {
          ...newError,
          [key]: { error: true, message: errorMessages[key] },
        };
      } else {
        newError = { ...newError, [key]: { error: false, message: "" } };
      }
      } else {
        newError = { ...newError, [key]: { error: false, message: "" } };
      }
    }
    return newError;
  };

  const validateEmail = async (email: string) => {  
    try {
      const response = await fetch(
        `${API_URL}/public/email/validator`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({email: email}),
        }
      )

      if (response.ok) {
        setFormError({ ...formError, email: { error: false, message: "" } });
        return true;
      } else {
        console.warn("⚠️ Validación fallida con status:", response.status);
        setFormError({ ...formError, email: { error: true, message: errorMessages.email } });
        return false;
      }
    } catch (error) {
      return false
    }
  };

  const validations = async (data: any) => {
    let newError: any = {};
    for (const key of Object.keys(data)) {
      newError = await fieldValidation({ data, key, newError });
    }
    let sumValidate = 0;
    Object.keys(newError).forEach((key) => {
      if (!newError[key].error) {
        sumValidate += 1;
      }
    });
    return sumValidate === Object.keys(newError).length ? undefined : newError;
  };

  const signupOrganic = async (lead: string, dataSecond: any) => {
    let dataOrganic = {
      leadId: lead,
      birthDate: formDataFirst.birthDate,
      campaign: HasSeriousIllnesses ? "calculator-cms-f+eg" : "calculator-cms",
      capital:
        typeof formDataFirst.capital === "number"
          ? formDataFirst.capital
          : parseInt(formDataFirst.capital),
      email: dataSecond.email,
      phone: dataSecond.phone,
      haveInsurance: formDataFirst.haveInsurance,
      ucc: globalInfo.ucc ?? "",
      withSeriousIllness: HasSeriousIllnesses ? true : false,
    };

    const response = await fetch(
      `${API_URL}/public/lead/organic/es/calculator`,
      {
        method: "POST",
        body: JSON.stringify(dataOrganic),
      }
    );
    if (response.status === 409) {
      // El email ya tiene lead: se le manda al login. No hay alta nueva, así
      // que el llamante no debe emitir leadCreated ni navegar a la app.
      track("submission_error", { error_type: "conflict", calculator: "es" });
      navigateToApp(buildAppHandoffUrl(process.env.GATSBY_APP_LOGIN_URL, {}));
      return { handedOff: true as const };
    }
    if (!response.ok) {
      throw new LeadSubmissionError(response.status);
    }
    return { handedOff: false as const };
  };

  const fetchOrganic = async (dataSecond : any) => {
    setLoading(true);
    try {
      const secondStepErrors = await validations(dataSecond);
      if (secondStepErrors) {
        throw secondStepErrors;
      }

      const lead = ulid();

      const signup = await signupOrganic(lead, dataSecond);
      if (signup.handedOff) {
        setLoading(false);
        return;
      }

      // A partir de aquí el lead EXISTE en el backend: solo entonces se
      // identifica, se emite leadCreated y se hace el handoff a la app.
      setLeadId(lead);

      identifyFS({
        lead: lead,
        data: {
          emailDomain: dataSecond.email && dataSecond.email.split("@")[1],
          email: dataSecond.email,
          brokerId: null,
        },
      });

      const cleanedPhone = (dataSecond.phone || "").replace(/[\s\-\(\)]/g, "");
      const formattedPhone = cleanedPhone.startsWith("+")
        ? cleanedPhone
        : `+34${cleanedPhone.replace(/^0+/, "")}`;

      const eventData = {
        page: globalInfo.url,
        email: dataSecond.email,
        phone: formattedPhone,
        leadId: lead,
        calculatedCapital: typeof formDataFirst.capital === "number"
          ? formDataFirst.capital
          : parseInt(formDataFirst.capital),
        utm_campaign: globalInfo.utm_campaign,
        utm_medium: globalInfo.utm_medium,
        utm_source: globalInfo.utm_source,
        utm_content: globalInfo.utm_content,
        utm_term: globalInfo.utm_term,
        utm_test: globalInfo.utm_test,
        gclid: globalInfo.gclid,
        gbraid: globalInfo.gbraid,
        wbraid: globalInfo.wbraid,
        msclkid: globalInfo.msclkid,
        fbclid: globalInfo.fbclid,
        entryPage: globalInfo.entryPage,
        referrerUrl: globalInfo.referrerUrl,
        ABTestVersion: globalInfo.ABTestVersion,
        platform: "App",
      };

      const [hashedEmail, hashedPhone] = await Promise.all([
        sha256(dataSecond.email),
        sha256(formattedPhone),
      ]);

      const leadCreatedEventData = {
        ...eventData,
        email: hashedEmail,
        phone: hashedPhone,
      };

      // Fire tracking events in parallel without blocking navigation
      Promise.all([
        handleTrack({ type: "tracker", data: { leadUlid: lead, campaign: globalInfo.gtf_campaign || "calculator-cms" } }),
        handleTrack({ type: "FS", event: "leadCreated", data: { ...leadCreatedEventData } }),
        handleTrack({ type: "GA", data: { event: "leadCreated", ...leadCreatedEventData } }),
       ]).catch(() => {});

      // First-party tracking
      track("lead_created", { leadId: lead, email: dataSecond.email, ...eventData });
      track("cross_domain_redirect", { destination: "app", leadId: lead }, true);

      if (typeof window !== "undefined") {
        // Cookies do not cross to app.life5.es, so the attribution captured on
        // the marketing site travels in the query string.
        navigateToApp(
          buildAppHandoffUrl(data[0].Url, {
            lead: lead,
            gtf_campaign: data[0]?.Promo,
          })
        );
      }

      setLoading(false);
    } catch (error) {
      if (error instanceof LeadSubmissionError || error instanceof Error) {
        // Fallo del backend o de red: el lead no se ha creado. Mensaje visible
        // y evento con el status para poder alertar sobre él.
        track("submission_error", {
          error_type: error instanceof LeadSubmissionError ? "backend" : "network",
          status: error instanceof LeadSubmissionError ? error.status : undefined,
          calculator: "es",
        });
        setFormError({ ...formError, submission: { error: true, message: errorMessages.submission } });
      } else {
        // Errores de validación de campos (objeto por campo).
        setFormError(error);
      }
      setLoading(false);
    }
  };

  const handleButton = async (scrollToTop = false) => {
    if (scrollToTop) {
      window.scrollTo({ top: 0, left: 0, behavior: "smooth" });
    }
    if (actualStep === 1) return await ValidateStepOneAndGoToNextStep();
    if (actualStep === 2) return await ValidateStepTwoAndGoToNextStep();
  };

  const ValidateStepOneAndGoToNextStep = async () => {
    const firstStepErrors = await validations(formDataFirst);
    if (firstStepErrors) {
      setFormError(firstStepErrors);
    } else {
      track("step_completed", { step: 1, calculator: "es", time_on_step_ms: Date.now() - stepStartTime.current });
      stepStartTime.current = Date.now();
      focusedFields.current.clear();
      setActualStep(2);
    }
  };

  const ValidateStepTwoAndGoToNextStep = async () => {
    let dataSecond = formDataSecond;
    if (formDataSecond.email === "") {
      let newEmail = (document.getElementById("emailInput") as HTMLInputElement)
        .value;
      setFormDataSecond({ ...formDataSecond, email: newEmail });
      dataSecond = { ...formDataSecond, email: newEmail };
    }
    if (formDataSecond.phone === "") {
      let newPhone = (document.getElementById("phoneInput") as HTMLInputElement)
        .value;
      setFormDataSecond({ ...formDataSecond, phone: newPhone });
      dataSecond = { ...formDataSecond, phone: newPhone };
    }
    const secondErrors = await validations(dataSecond);
    if (secondErrors) return setFormError(secondErrors);
    await fetchOrganic(dataSecond);
  };

  const GeneratePhoneValidationCode = async () => {
    await fetch(`${API_URL}/public/lead/phone/generate-code`, {
      method: "POST",
      body: JSON.stringify({
        phone: formDataSecond.phone,
      }),
    });
  };

  const RegeneratePhoneValidationCode = async () => {
    await PhoneValidationTrackingEvent({
      eventName: "PhoneValidationCodeRegenerated",
    });
    await GeneratePhoneValidationCode();
  };

  const ValidatePhoneValidationCode = async (code: string) => {
    const isValid = await fetch(`${API_URL}/public/lead/phone/validate`, {
      method: "POST",
      body: JSON.stringify({
        phone: formDataSecond.phone,
        code: code,
      }),
    });
    if (isValid.status === 200) {
      await PhoneValidationTrackingEvent({ eventName: "PhoneValidationValid" });
      let dataSecond = formDataSecond;
      if (formDataSecond.email === "") {
        let newEmail = (
          document.getElementById("emailInput") as HTMLInputElement
        ).value;
        setFormDataSecond({ ...formDataSecond, email: newEmail });
        dataSecond = { ...formDataSecond, email: newEmail };
      }
      await fetchOrganic(dataSecond);
      return { error: false, message: "" };
    }
    await PhoneValidationTrackingEvent({ eventName: "PhoneValidationInvalid" });
    return { error: true, message: "El código introducido no es válido" };
  };

  const PhoneValidationTrackingEvent = async ({ eventName }) => {
    const dataInfo = {
      event: eventName,
      page: globalInfo.url,
      leadId: null,
      phone: formDataSecond.phone,
      email: formDataSecond.email,
      utm_campaign: globalInfo.utm_campaign,
      utm_medium: globalInfo.utm_medium,
      utm_source: globalInfo.utm_source,
      utm_content: globalInfo.utm_content,
      utm_term: globalInfo.utm_term,
      utm_test: globalInfo.utm_test,
      gclid: globalInfo.gclid,
      gbraid: globalInfo.gbraid,
      wbraid: globalInfo.wbraid,
      msclkid: globalInfo.msclkid,
      fbclid: globalInfo.fbclid,
      entryPage: globalInfo.entryPage,
      referrerUrl: globalInfo.referrerUrl,
      ABTestVersion: globalInfo.ABTestVersion,
      platform: "App",
    };

    await handleTrack({ type: "GA", data: dataInfo });

    await handleTrack({
      type: "FS",
      event: eventName,
      data: dataInfo,
    });
  };

  const handleChange = async ({ setValue, value, field, formData }: any) => {
    let newData = { ...formData, [field]: value };
    // For email and phone: clear error on change, validate only on blur
    if ((field === "email" || field === "phone") && formError?.[field]?.error) {
      setFormError({ ...formError, [field]: { error: false, message: "" } });
    } else if (value && formError && formError[field] && formError[field].error) {
      // For other fields: validate on change if there was a previous error
      let newError: any = {};
      newError = await fieldValidation({ data: newData, key: field, newError });
      setFormError({ ...formError, ...newError });
    }
    await setValue({...formData, [field]: value});
  };

  const handleBlur = async ({ field, formData }: any) => {
    let newError: any = {};
    if (!!formData[field]) {
      newError = await fieldValidation({ data: formData, key: field, newError });
      setFormError({ ...formError, ...newError });
      if (newError[field]?.error) {
        track("validation_error", { field, error_type: field === "email" ? "api" : "format", step: actualStep, calculator: "es" });
      } else {
        track("field_completed", { field, step: actualStep, calculator: "es" });
      }
    }
  };

  useEffect(() => {
    if (typeof window !== "undefined" && typeof document !== "undefined") {
      const newQuery = new URLSearchParams(window.location.search);

      setGlobalInfo({
        defaultcap: newQuery.get("defaultcap"),
        gtf_campaign: getCookie("gtf_campaign"),
        utm_source: getCookie("utm_source"),
        utm_medium: getCookie("utm_medium"),
        utm_campaign: getCookie("utm_campaign"),
        tid: getCookie("tid"),
        utm_content: getCookie("utm_content"),
        utm_term: getCookie("utm_term"),
        utm_test: getCookie("utm_test"),
        gclid: getCookie("gclid"),
        gbraid: getCookie("gbraid"),
        wbraid: getCookie("wbraid"),
        msclkid: getCookie("msclkid"),
        fbclid: getCookie("fbclid"),
        entryPage: getCookie("entryPage"),
        referrerUrl: getReferrerUrl(),
        ABTestVersion: getCookie("ABTestVersion"),
        url: window.location.href,
        ucc: newQuery.get("ucc")
      });
    }

    if (actualStep === 2) {
      handleFocus();
    }
  }, [cookies, actualStep]);

  useEffect(() => {
    (async () => {
      if (formDataFirst.birthDate) {
        if (typeof window !== "undefined") {
          try {
            await fetch(
              `${API_URL}/public/age/validation/${formDataFirst.birthDate}`
            ).then((response) =>  {
              if (response.status === 400) {
                throw new Error;
              } else {
                return response.json()
              }
            }) 

            const capitals = await fetch(
              HasSeriousIllnesses
                ? `${API_URL}/public/boundaries/serious-illnesses/${formDataFirst.birthDate}`
                : `${API_URL}/lead/capital/tarifier/pureBoundaries/${formDataFirst.birthDate}`
            )
              .then((response) => response.json())
              .then((resultData) => {
                return resultData;
              });

            const { options, indexPreSelected } = prepareCapitals({ capitals });

            setCapitalOptions(options);

            if (globalInfo.defaultcap) {
              let capital = options.find(
                (element) => element.value === parseInt(globalInfo.defaultcap!)
              );

              if (capital) {
                setFormDataFirst({
                  ...formDataFirst,
                  capital: capital!.value,
                });
                setFormError({
                  ...formError,
                  birthDate: { error: false, message: "" },
                });
              } else {
                setFormDataFirst({
                  ...formDataFirst,
                  capital: options[indexPreSelected].value,
                });
                setFormError({
                  ...formError,
                  birthDate: { error: false, message: "" },
                });
              }
            } else {
              setFormDataFirst({
                ...formDataFirst,
                capital: options[indexPreSelected].value,
              });
              setFormError({
                ...formError,
                birthDate: { error: false, message: "" },
              });
            }
          } catch (error: any) {
            console.log("error", error);
            if (error.error) {
              setFormError({ ...formError, birthDate: error });
            } else {
              setFormError({
                ...formError,
                birthDate: { error: true, message: errorMessages.birthDate },
              });
            }
            setCapitalOptions([]);
          }
        }
      }
    })();
  }, [formDataFirst.birthDate]);

  useEffect(() => {
    if (typeof document !== "undefined") {
      setCookie(document.cookie);
    }
  }, [actualStep]);

  useEffect(() => {
    // `ReactGA.initialize("UA-191842015-1")` used to run here. Universal
    // Analytics was switched off by Google in July 2023, so the only thing it
    // still did was download and execute the dead analytics.js on every
    // calculator page. Removed with the react-ga dependency (2026-09-14); GA4
    // is served by the GTM container.
    track("calculator_viewed", { calculator: "es" });
  }, []);

  useEffect(() => {
    const handleVisibilityChange = () => {
      const payload = {
        last_step: actualStep,
        calculator: "es",
        time_on_step_ms: Date.now() - stepStartTime.current,
      };
      if (document.visibilityState === "hidden") {
        track("session_pause", payload, true);
      } else {
        track("session_resume", { last_step: actualStep, calculator: "es" });
      }
    };

    const handlePageHide = () => {
      track("session_end", {
        last_step: actualStep,
        calculator: "es",
        time_on_step_ms: Date.now() - stepStartTime.current,
      }, true);
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [actualStep]);

  const VWOID = getCookie("_vwo_uuid_v2");
  const GAID = getCookie("_ga");
  const AT_GD = getCookie("at_gd");
  const API_URL = process.env.GATSBY_API_URL;

  const UTM_CAMPAIGN = getCookie("utm_campaign");
  const UTM_SOURCE = getCookie("utm_source");
  const UTM_MEDIUM = getCookie("utm_medium");
  const UTM_CONTENT = getCookie("utm_content");
  const UTM_TERM = getCookie("utm_term");
  const UTM_TEST = getCookie("utm_test");
  const GCLID = getCookie("gclid");
  const GBRAID = getCookie("gbraid");
  const WBRAID = getCookie("wbraid");
  const MSCLKID = getCookie("msclkid");
  const FBCLID = getCookie("fbclid");
  const TID = getCookie("tid");
  const ENTRYPAGE = getCookie("entryPage");
  const REFERRERURL = getReferrerUrl();
  const ABTESTVERSION = getCookie("ABTestVersion");

  const handleChangeDataTracker = async ({
    leadUlid,
    campaign,
  }: DataTrackerI) => {
    let data = {
      leadUlid: leadUlid,
      googleAnalyticsId: GAID,
      fullstoryId: "",
      vwoId: VWOID,
      utmCampaign: UTM_CAMPAIGN,
      utmSource: UTM_SOURCE,
      utmMedium: UTM_MEDIUM,
      utmContent: UTM_CONTENT,
      utmTerm: UTM_TERM,
      utmTest: UTM_TEST,
      gclid: GCLID,
      gbraid: GBRAID,
      wbraid: WBRAID,
      msclkid: MSCLKID,
      fbclid: FBCLID,
      tid: TID,
      entryPage: ENTRYPAGE,
      referrerUrl: REFERRERURL,
      aBTestVersion: ABTESTVERSION,
      campaign,
      atGd: AT_GD,
    };
    try {
      await fetch(`${API_URL}/lead/track/v2`, {
        method: "POST",
        body: JSON.stringify(data),
      })
        .then((response) => response.json()) // parse JSON from request
        .then((resultData) => {
          return resultData;
        });
    } catch (error) {
      console.log(error);
    }
  };

  const sendFullStoryEvent = async ({ FsEvent, data = {} }: FsEventI) => {
    posthogCapture(FsEvent, data);
    let dataEvent = {
      leadUlid: data.leadId,
      eventType: FsEvent,
      platform: "fullstory",
      createdAt: new Date(),
      payload: data,
    };

    await fetch(`${process.env.GATSBY_APP_EVENTS_API}public/front/event`, {
      method: "POST",
      body: JSON.stringify(dataEvent),
    });
  };

  const handleChangeDataLayer = async (newData: any) => {
    // Was `TagManager.dataLayer({ dataLayer: newData })`: react-gtm-module is a
    // thin wrapper that does exactly this push when window.dataLayer exists,
    // which it always does (seeded inline in <head> by gatsby-ssr.js).
    pushToDataLayer(newData);
    let dataEvent = {
      leadUlid: newData.leadId,
      eventType: newData.event,
      platform: "ga4",
      createdAt: new Date(),
      payload: newData,
    };

    await fetch(`${process.env.GATSBY_APP_EVENTS_API}public/front/event`, {
      method: "POST",
      body: JSON.stringify(dataEvent),
    });
  };

  const identifyFS = ({ lead, data }: IdentifyFSI) => {
    posthogIdentify(
      lead, // Required. Replace 'distinct_id' with your user's unique identifier
      { email: data.email }, // $set, optional
      { data } // $set_once, optional
    );
  };

  const handleTrack = async ({ type, event, data }: HandleTrackerI) => {
    const trackers = {
      FS: () => sendFullStoryEvent({ FsEvent: event!, data }),
      tracker: () => handleChangeDataTracker(data),
      GA: () => handleChangeDataLayer(data),
    };

    return isNotDEV && trackers[type]();
  };

  useEffect(() => {
    const snackbar = document.getElementById("snackbar_module");
    if (snackbar) snackbar.style.display = isMobile ? "none" : "inherit";
  }, [isMobile]);

  useEffect(() => {
    if(HasSeriousIllnesses) setCookieHook("gtf_campaign", null);
  }, [HasSeriousIllnesses])

  const trackStepBack = (from: number, to: number) => {
    track("step_back", { from, to, calculator: "es" });
    stepStartTime.current = Date.now();
    focusedFields.current.clear();
  };

  return {
    actualStep,
    capitalOptions,
    display,
    formDataFirst,
    formDataSecond,
    formError,
    handleBlur,
    handleButton,
    handleChange,
    handleFocus,
    HasSeriousIllnesses,
    isDesktop,
    isMobile,
    isTablet,
    loading,
    optionsHaveInsurance,
    setActualStep,
    setDisplay,
    setFormDataFirst,
    setFormDataSecond,
    trackStepBack,
    validations,
    refPhoneValidationModal,
    RegeneratePhoneValidationCode,
    ValidatePhoneValidationCode,
  };
};

export default CalculatorModuleController;
